import {AccessCodeAlphabet, LogLevel, Occupancy} from "@variocube/app-api";
import {
	BoxStorage,
	BoxTypeSelectionResult,
	MessageView,
	PendingView,
	PushButton,
	useAsyncCallback,
	useBoxes,
	useOccupancies,
	usePlatform,
	usePreventDeactivate,
	useStorageItemValue,
} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {useLocalization} from "../i18n";
import {AddDeliveryToBoxView} from "./AddDeliveryToBoxView";
import {compareRecipients} from "./compareRecipients";
import {logger} from "./logger";
import {SizeSelectionView} from "./SizeSelectionView";
import {Delivery, DeliveryState, getOccupancyDeliveries, Tenant, TenantUser, UseExistingBox} from "./types";

const accessCodeShape = {
	alphabet: AccessCodeAlphabet.OneToNine,
	length: 5,
};

export interface StorageFlowProps {
	delivery: Delivery;
	onDone: () => void;
	user?: TenantUser;
	disableAddToBox?: boolean;
}

const log = logger.sub("StorageFlow");

export function StorageFlow({delivery, onDone, user, disableAddToBox}: StorageFlowProps) {
	const [selectSize, setSelectSize] = useState(false);
	const [occupancies, setOccupancies] = useState<Occupancy[]>([]);
	const {t} = useLocalization();
	const platform = usePlatform();
	const allBoxes = useBoxes();
	const allOccupancies = useOccupancies();
	const tenant = useStorageItemValue<Tenant>("tenant");
	const useExistingBox = tenant?.useExistingBox ?? UseExistingBox.AskToAdd;

	/** Whether the user wants a separate box even though a delivery for the recipients is currently stored. */
	const [separateBox, setSeparateBox] = useState(false);

	const allowSplitStorage = tenant?.allowSplitStorage ?? true;

	const existingOccupanciesForRecipients = useMemo(() => {
		return allOccupancies.filter(occupancy => {
			const occupancyDeliveries = getOccupancyDeliveries(occupancy);
			if (occupancyDeliveries) {
				return occupancyDeliveries.every(occupancyDelivery =>
					compareRecipients(delivery.recipients, occupancyDelivery.recipients)
				);
			}
		});
	}, [tenant, allOccupancies, delivery]);

	const boxes = useMemo(
		() => allBoxes.filter(box => occupancies.find(occupancy => occupancy.boxNumber == box.number)),
		[allBoxes, occupancies],
	);

	usePreventDeactivate();

	const handleSelectOtherSize = useCallback(() => setSelectSize(true), []);

	useEffect(() => {
		if (UseExistingBox.Disabled === useExistingBox) {
			setSeparateBox(true);
		} else if (UseExistingBox.Always === useExistingBox) {
			handleAddDeliveryToBox();
		}
	}, [useExistingBox]);

	useEffect(() => {
		if (delivery.delivery.state == DeliveryState.Reserved) {
			platform.getOccupancies().then((allOccupancies) => {
				const reservedOccupancies = allOccupancies.filter((o) => {
					const occupancyDeliveries = getOccupancyDeliveries(o);
					if (occupancyDeliveries) {
						return occupancyDeliveries.every(occupancyDelivery =>
							occupancyDelivery.delivery.deliveryId == delivery.delivery.deliveryId
						);
					}
				});
				if (reservedOccupancies.length > 0) {
					setOccupancies(reservedOccupancies);
					setSelectSize(false);
				} else {
					setSelectSize(true);
				}
			});
		} else {
			setSelectSize(true);
		}
	}, [delivery]);

	/**
	 * occupy is called each time when a size is selected and box is opened (but not closed yet!)
	 */
	const occupy = useAsyncCallback(async (typeSelectionResult: BoxTypeSelectionResult) => {
		setSelectSize(false);
		const existing = [...occupancies].pop();
		const predefinedAccessCode = existing ? existing.accessCode : delivery.delivery.accessCode;
		const accessCodeRequest = predefinedAccessCode ? {accessCode: predefinedAccessCode} : {accessCodeShape};
		// We need to make sure that the delivery we write to the occupancy is in state stored. Only then will a reserved
		// occupancy be processed in the backend if the user had chosen to select a different box. Also, we can then use
		// the state in the occupancy to end the previously reserved occupancies.
		// And we need to make sure that we create a copy of the delivery to not change the state of the delivery that
		// is coming in as parameter.
		const copy: Delivery = {
			...delivery,
			delivery: {
				...delivery.delivery,
			},
		};
		if (!copy.delivery.state) {
			copy.delivery.state = DeliveryState.Stored;
		}

		const occupancy = await platform.occupy({
			...typeSelectionResult,
			content: {
				deliveries: [copy],
			},
			...accessCodeRequest,
		});
		log.info("Occupancy created", occupancy);

		setOccupancies(occupancies => [...occupancies, occupancy]);
	}, [occupancies, platform, delivery]);

	/**
	 * Cancel is called each time the user cancels the delivery process
	 */
	const cancel = useAsyncCallback(async () => {
		await Promise.all(occupancies.map(async (occupancy) => {
			try {
				await platform.cancelOccupancy(occupancy.uuid);
			} catch (err) {
				// if this was a reserved delivery, the occupancy was already confirmed
				// we should try to end the occupancy instead.
				if (delivery.delivery.state == DeliveryState.Reserved) {
					log.info("Ending occupancy of reserved delivery.");
					try {
						await platform.endOccupancy(occupancy.uuid);
					} catch (err) {
						log.error("Failed to end occupancy of reserved delivery.", err);
					}
				} else {
					log.error("Failed to cancel occupancy.");
				}
			}
		}));
		setOccupancies([]);
		onDone();
	}, [occupancies, platform, onDone]);

	const handleStorageConfirmation = useCallback(async () => {
		try {
			if (delivery.delivery.state !== DeliveryState.Returned) {
				delivery.delivery.state = DeliveryState.Stored;
			}
			if (user) {
				delivery.delivery.storedBy = user.username;
			}

			for (const occupancy of occupancies) {
				if (existingOccupanciesForRecipients.indexOf(occupancy) >= 0) {
					log.info("Updating occupancy with additional delivery", occupancy);

					const deliveries = [
						...(occupancy.content.deliveries || [occupancy.content]),
						delivery,
					];

					await platform.updateOccupancy(occupancy.uuid, {deliveries});
				}
				else {
					log.info("Confirming occupancy", occupancy);
					const deliveries = [delivery];
					await platform.confirmOccupancy(occupancy.uuid, {deliveries}, true);
				}
				await platform.log(LogLevel.Audit, "Staff Store", {occupancy, user});
			}
			onDone();
		} catch (error) {
			log.error("Error confirming occupancy", error);
			throw error;
		}
	}, [delivery, platform, user, occupancies, existingOccupanciesForRecipients, log, onDone]);

	const handleReservedConfirmation = useCallback(async () => {
		log.info(
			"Delivery {} is in state Reserved  (need to confirm occupancy when locker changed)",
			delivery.delivery.deliveryId,
		);
		let firstAccessCode = undefined;
		for (const occupancy of occupancies) {
			try {
				await platform.confirmOccupancy(occupancy.uuid);
				log.info("Occupancy confirmed", occupancy);
			} catch (error) {
				log.error("Error confirming occupancy", error);
			}

			const deliveries = getOccupancyDeliveries(occupancy);
			if (!deliveries) {
				throw new Error(`Occupancy ${occupancy.uuid} does not contain valid delivery data`);
			}

			for (const occupancyDelivery of deliveries) {
				if (occupancyDelivery.delivery.state !== DeliveryState.Returned) {
					occupancyDelivery.delivery.state = DeliveryState.Stored;
				}
				occupancyDelivery.delivery.boxNumber = occupancy.boxNumber; // update box nr for the delivery json
			}

			if (firstAccessCode) {
				await platform.changeAccessCode(occupancy.uuid, {accessCode: firstAccessCode});
			} else {
				await platform.changeAccessCode(occupancy.uuid, {accessCodeShape});
				const occupancyWithAccessCode = await platform.getOccupancy(occupancy.uuid);
				firstAccessCode = occupancyWithAccessCode?.accessCode;
			}

			await platform.updateOccupancy(occupancy.uuid, {deliveries});
		}
		onDone();
	}, [delivery, platform, occupancies, log, onDone]);

	/**
	 * Confirm is called each time all the opened boxes are closed
	 */
	const confirm = useAsyncCallback(async () => {
		const eligibleState =
			[
				DeliveryState.Created,
				DeliveryState.Stored,
				DeliveryState.Returned
			]
				.includes(delivery.delivery.state);
		const reservedState = delivery.delivery.state == DeliveryState.Reserved;

		if (eligibleState) {
			await handleStorageConfirmation();
		}
		else if (reservedState) {
			await handleReservedConfirmation();
		}
	}, [delivery, handleStorageConfirmation, handleReservedConfirmation]);

	const handleSizeBack = useCallback(() => {
		if (occupancies.length > 0) {
			setSelectSize(false);
		} else {
			onDone();
		}
	}, [occupancies, onDone]);

	const handleAddDeliveryToBox = useCallback(() => {
		if (existingOccupanciesForRecipients.length > 0) {
			setOccupancies(existingOccupanciesForRecipients);
			setSelectSize(false);
		} else {
			setSelectSize(true);
		}
	}, [existingOccupanciesForRecipients]);

	/**
	 * When the user decides to choose another box.
	 */
	const handleWrongSizeAndMaintenance = useCallback(async () => {
		try {
			for (let o of occupancies) {
				const deliveries = getOccupancyDeliveries(o);
				if (deliveries && deliveries.filter((d) => d.delivery.state == DeliveryState.Reserved).length > 0) {
					await platform.endOccupancy(o.uuid);
				} else {
					await platform.cancelOccupancy(o.uuid);
				}
			}
			delivery.delivery.state = DeliveryState.Created;
			setOccupancies([]);
			setSelectSize(true);
		} catch (error) {
			console.error("Error cancelling occupancy after box unusable", error);
		}
	}, [platform, occupancies]);

	const handleMaintenance = useCallback(async () => {
		await handleWrongSizeAndMaintenance();
		occupancies.map(async (o) => {
			try {
				await platform.requireBoxMaintenance(o.boxNumber);
			} catch (error) {
				console.error("Error setting Box for maintenance.", error);
			}
		});
	}, [occupancies]);

	if (
		!disableAddToBox
		&& delivery.delivery.state != DeliveryState.Returned
		&& occupancies.length == 0
		&& !separateBox
		&& existingOccupanciesForRecipients.length > 0
	) {
		return (
			<AddDeliveryToBoxView
				onHandleAddDeliveryToBox={() => handleAddDeliveryToBox()}
				onSeparateBox={() => setSeparateBox(true)}
			/>
		);
	}

	if (selectSize) {
		return <SizeSelectionView onBack={handleSizeBack} onSelect={occupy.execute} />;
	}

	if (occupy.error) {
		return (
			<MessageView message={t("BoxTypeNotAvailable")}>
				<PushButton primary onClick={handleSelectOtherSize}>
					{t("SelectOtherSize")}
				</PushButton>
				<PushButton onClick={cancel.execute}>
					{t("Cancel")}
				</PushButton>
			</MessageView>
		);
	}

	if (confirm.error) {
		return (
			<MessageView message={`Error confirming occupancies: ${confirm.error}`}>
				<PushButton onClick={cancel.execute}>
					{t("Cancel")}
				</PushButton>
			</MessageView>
		);
	}

	if (occupy.loading) {
		return <PendingView message={t("OccupancyPending")} />;
	}

	if (occupancies.length || boxes.length) {
		const boxNames = boxes.map(box => box.number).join(", ");

		return (
			<BoxStorage
				platform={platform}
				boxes={boxes}
				onCancel={cancel.execute}
				onConfirm={confirm.execute}
				onWrongSize={handleWrongSizeAndMaintenance}
				onMaintenance={handleMaintenance}
				strings={t}
				boxOpenTitle={t("DeliveryStoreTitle")}
				boxOpenMessage={t("DeliveryStoreMessage", {boxNames})}
				confirmTitle={t("DeliveryConfirmTitle")}
				confirmMessage={t("DeliveryConfirmMessage", {boxNames})}
				openActions={
					<Fragment>
						{allowSplitStorage && (
							<PushButton onClick={handleSelectOtherSize}>
								{t("DeliveryAddLocker")}
							</PushButton>
						)}
						<PushButton onClick={cancel.execute}>
							{t("Cancel")}
						</PushButton>
					</Fragment>
				}
				showBoxUnusable={existingOccupanciesForRecipients.length == 0 || separateBox}
				autoConfirm={tenant?.storageCountDown ?? undefined}
			/>
		);
	}

	return null;
}
